/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
// utils

// material
import { Page, Content, Division, InContentMessage } from '@insticator/insticator-ui'
import Header from '../../../../material/structure/header/header'
import GlobalFilter from '../../../../material/globalfilter/globalfilter'
import PageHeader from '../../../../material/structure/pageheader/pageheader'
import RevenueVisualization from '../revenue_beta/revenue_beta'

// styles
import injectSheet from 'react-jss'
import styles from './videoadmanagement_styles'

// static
import {
  NET_REVENUE,
  GROSS_REVENUE,
  AVG_RPM,
  AVG_CPM,
  SITE,
  DEVICE,
  GEO,
  PRODUCT_TYPE,
  TOTAL_IMPRESSIONS,
  BAR_CHART,
  LINE_CHART,
  VIEWABILITY,
  AD_UNIT,
  VIDEO_COMPLETION_RATE,
  TOOLTIP_CONTENTS, PAGEVIEW_TYPE, VIDEO
} from '../consts'

/* ========== ~~~~~~~~~~ MONETIZATION - VIDEO AD PAGE ~~~~~~~~~~ ========== */
const VideoAdManagementPage = injectSheet(styles)((props) => {
  const accountUUID = useSelector((state) => state.session.accountUUID)
  const startDate = useSelector((state) => state.app.filters.dateRange.from)
  const endDate = useSelector((state) => state.app.filters.dateRange.to)
  const compareTo = useSelector((state) => state.app.filters.compareTo)
  const devices = useSelector((state) => state.app.filters.devices)
  const countries = useSelector((state) => state.app.filters.countries)
  const siteUUIDs = useSelector((state) => state.app.filters.sites)
  const lastUpdated = useSelector((state) => state.revenueBeta.lastUpdated)
  const mocked = useSelector((state) => state.session.mocked)
  const { showMessage, message } = useSelector((state) => state.settings.notificationsConfig)

  const { enableCpmAdMgmt, enableInternalMetrics } = useSelector((state) => state.account.accountSettings)

  const [from, setFrom] = useState(startDate)
  const [to, setTo] = useState(endDate)
  const [compare, setCompare] = useState(compareTo)

  const cardtoggles = {
    revenue: [
      { id: NET_REVENUE, label: 'Publisher Video Revenue' }
    ],
    rpmCpm: [
      { id: AVG_RPM, label: 'Avg Video RPM' }
    ],
    impression: [
      { id: TOTAL_IMPRESSIONS, label: 'Video Ad Impressions' }
    ],
    viewability: [
      { id: VIEWABILITY, label: 'Avg Video Viewability' }
    ]
  }
  const baseParams = type => {
    const productType = type === 'pageview' ? PAGEVIEW_TYPE : PRODUCT_TYPE
    const productTypeFilter = type === 'pageview' ? [VIDEO] : ['VIDEO', 'IN_STREAM_VIDEO', 'OUT_STREAM_VIDEO']
    return {
      accountUUID,
      startDate: from,
      endDate: to,
      filters: {
        [SITE]: siteUUIDs.length ? siteUUIDs : undefined,
        [DEVICE]: devices.length ? devices : undefined,
        [GEO]: countries.length ? countries : undefined,
        [productType]: productTypeFilter
      },
      limit: 5,
      compareTo: compare
    }
  }
  const params = {
    revenue: {
      ...baseParams('revenue'),
      dimension: PRODUCT_TYPE,
      metrics: [NET_REVENUE],
      initialMetric: NET_REVENUE
    },
    rpmCpm: {
      ...baseParams('pageview'),
      dimension: DEVICE,
      metrics: [AVG_RPM],
      initialMetric: AVG_RPM
    },
    impression: {
      ...baseParams('revenue'),
      dimension: PRODUCT_TYPE,
      metrics: [TOTAL_IMPRESSIONS],
      initialMetric: TOTAL_IMPRESSIONS
    },
    viewability: {
      ...baseParams('revenue'),
      dimension: AD_UNIT,
      metrics: [VIEWABILITY],
      initialMetric: VIEWABILITY
    }
  }
  const allowedDimensions = {
    revenue: [SITE, DEVICE, GEO, PRODUCT_TYPE, AD_UNIT],
    rpmCpm: [SITE, DEVICE, GEO],
    impression: [SITE, DEVICE, GEO, PRODUCT_TYPE, AD_UNIT],
    viewability: [SITE, DEVICE, GEO, PRODUCT_TYPE, AD_UNIT]
  }

  // internal only
  if (enableInternalMetrics && mocked) {
    cardtoggles.revenue.push({ id: GROSS_REVENUE, label: 'Gross Video Revenue' })
    params.revenue.metrics.push(GROSS_REVENUE)
  }

  if (enableCpmAdMgmt) {
    cardtoggles.rpmCpm.push({ id: AVG_CPM, label: 'Avg Video CPM' })
    params.rpmCpm.metrics.push(AVG_CPM)
  }

  return (
        <Division name={'videoadmanagement'}>
            <Page name={'videoadmanagement'} klass={`${props.classes.videoadmanagement}`} >
                <Header>
                    <GlobalFilter onFilterApply={() => {
                      setFrom(startDate)
                      setTo(endDate)
                      setCompare(compareTo)
                    }} />
                </Header>
                <Content>
                    <InContentMessage />
                    <PageHeader lastUpdatedTimestamp={lastUpdated}
                                title={'Monetization'}
                                subtitle={'Video Ad Management'}
                                dataTip={true}
                                bannerText={showMessage ? message : null}
                                ignoreDataTip={showMessage}
                    />
                    <RevenueVisualization
                        params={params.revenue}
                        cardtogglesList={cardtoggles.revenue}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.revenue}
                        title={cardtoggles.revenue[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.videoRevenue}
                    />
                    <RevenueVisualization
                        params={params.rpmCpm}
                        cardtogglesList={cardtoggles.rpmCpm}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.rpmCpm}
                        title={cardtoggles.rpmCpm[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.videoRpmCpm}
                    />
                    <RevenueVisualization
                        params={params.impression}
                        cardtogglesList={cardtoggles.impression}
                        chartType={BAR_CHART}
                        allowedDimensions={allowedDimensions.impression}
                        title={cardtoggles.impression[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.videoImpression}
                        zAxisId={VIDEO_COMPLETION_RATE}
                        zAxisLabel={'Video Completion Rate'}
                    />
                    <RevenueVisualization
                        params={params.viewability}
                        cardtogglesList={cardtoggles.viewability}
                        chartType={LINE_CHART}
                        allowedDimensions={allowedDimensions.viewability}
                        title={cardtoggles.viewability[0].label}
                        tooltipContent ={TOOLTIP_CONTENTS.videoViewability}
                    />
                </Content>
            </Page>
        </Division>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default VideoAdManagementPage
